<template>
  <a-collapse
    v-model:activeKey="activeKey"
    :bordered="false"
    class="custom-collapse"
    style="background: rgb(255, 255, 255)"
  >
    <template #expandIcon="{ isActive }">
      <a-row style="text-align: left; min-width: 25rem">
        <a-col :span="24">
          <span class="task-label" style="margin-right: 1rem">
            Задача {{ task.number }}
          </span>
          <img
            src="@/assets/icons/circle-chevron-right.svg"
            alt=""
            class="collapsed"
            :class="!isActive ? 'active' : ''"
          />
        </a-col>
        <a-col
          :span="24"
          class="task-name"
          :class="preview && 'preview'"
          style="margin-top: 1rem"
        >
          {{ task.name }}

          <span class="edit-button" v-if="checkIfEditable">
            <img
              src="@/assets/icons/edit.svg"
              @click.stop="checkIfEditable && showForm(FORM_NAME_TASK_EDIT)"
              alt=""
            />
          </span>
        </a-col>
      </a-row>
    </template>

    <a-collapse-panel key="1" class="task-card">
      <template #extra>
        <div class="task-card__status-bar">
          <progress-bar :items="getCheckpoints(task)" incompleteColor="white" />
          <indicators-block v-if="isShowingIndicators">
            <indicator-item v-if="isShowingVolunteering">В</indicator-item>
            <indicator-item v-if="isShowingCompetencies">К</indicator-item>
          </indicators-block>
        </div>
      </template>

      <div :class="preview && 'preview'">
        <template v-if="task.checkpoints">
          <check-points
            :taskId="task.id"
            :checkpoints="task.checkpoints"
            :disabled="!checkIfEditable"
            @update="onUpdated"
          />

          <a-row class="task-executor">
            <a-col :span="24">
              Исполнитель:
              <span class="task-executor__fullname">{{ task.executor }} </span>
            </a-col>
          </a-row>
        </template>

        <template v-if="task.subtasks">
          <sub-task
            v-for="(subtask, index) in task.subtasks"
            :key="index"
            :task="{ ...subtask, number: `${task.number}.${index + 1}` }"
            :disabled="!checkIfSubTaskEditable(subtask)"
            @updated="onUpdated"
          />
        </template>

        <template v-if="checkIfEditable">
          <a-divider />

          <a-row class="task-card__add-subtask">
            <a-col :span="12">
              <a @click="showForm(FORM_NAME_TASK_ADD)">Создать подзадачу</a>
            </a-col>
            <a-col
              :span="12"
              class="task-card__partner-visibility"
              v-if="isLeader"
            >
              <c-checkbox
                v-if="showAvailability"
                v-model:checked="isVisible"
                @click="changeAvailability"
              >
                Сделать задачу видимой для партнера
              </c-checkbox>
            </a-col>
          </a-row>
        </template>
      </div>
    </a-collapse-panel>
  </a-collapse>
  <drawer-right v-model="isShowingDrawer" @close="onDrawerClose">
    <template v-if="FORM_NAME_TASK_ADD === formName">
      <task-form
        :parentId="parentId"
        :projectId="parseInt(route.params.id)"
        @updated="onUpdated"
      />
    </template>
    <template v-else-if="FORM_NAME_TASK_EDIT === formName">
      <task-form
        :task="task"
        @updated="onUpdated"
        @delete-action="showForm(FORM_NAME_TASK_DELETE)"
      />
    </template>
    <template v-else>
      <delete-task
        :task="task"
        @deleted="onUpdated"
      />
    </template>
  </drawer-right>
</template>

<script setup>
  import { computed, onMounted, ref, watch } from 'vue';
  import ProgressBar from '@/components/projects/management/ProgressBar.vue';
  import CheckPoints from '@/components/projects/management/CheckPoints/CheckPoints.vue';
  import CCheckbox from '@/components/UI/CCheckbox';
  import DeleteTask from '@/components/projects/management/Task/DeleteTask.vue';
  import SubTask from '@/components/projects/management/SubTask/SubTask.vue';
  import { useApi } from '@/api/use-api';
  import { CHECKPOINT_COMPETENCE_SET_STATUS_DEFAULT } from '@/constants/checkpoint-competence-set';
  import { USER_ROLE_LEADER } from '@/api/models/UserRole';
  import { useRoute } from 'vue-router';
  import { useStore } from 'vuex';
  import { FORM_NAME_TASK_ADD, FORM_NAME_TASK_DELETE, FORM_NAME_TASK_EDIT } from './constants/form-name';
  import TaskForm from './TaskForm.vue';
  import DrawerRight from '@/components/drawers/components/DrawerRight.vue';
  import IndicatorsBlock from '@/components/indicator/components/IndicatorsBlock.vue';
  import IndicatorItem from '@/components/indicator/components/IndicatorItem.vue';

  const props = defineProps({
    task: Object,
    preview: {
      required: false,
      type: Boolean,
    },
    disabled: {
      required: true,
      default: false,
    },
    showAvailability: {
      type: Boolean,
      default: true,
    },
    editorUserId: Number,
  });

  const emit = defineEmits(['update']);

  const api = useApi();
  const route = useRoute();
  const store = useStore();
  const isLeader = localStorage?.getItem('currentRole') === 'leader';
  const activeKey = ref([]);
  const isVisible = ref(props.task?.visibility);
  const hasUnconfirmedCompetence = ref(false);
  const formName = ref(null);
  const isShowingDrawer = ref(false);
  const parentId = computed(() => props.task.id);

  // показывать индикаторы только для руководителя проекта, и когда панель с задачей закрыта
  const isShowingIndicators = computed(() => {
    return USER_ROLE_LEADER === route.meta.role && 0 === activeKey.value.length;
  });

  // показывать ли индикатор волонтерства
  const isShowingVolunteering = computed(() => {
    return isShowingIndicators.value && props.task.hasUnconfirmedVolunteering;
  });

  // показывать ли индикатор компетенций
  const isShowingCompetencies = computed(() => {
    return (
      isShowingIndicators.value &&  
      store.getters.enableCompetencies && 
      hasUnconfirmedCompetence.value
    );
  });

  watch(formName, () => {
    isShowingDrawer.value = null !== formName.value;
  });

  const getCheckpoints = (task) => {
    // TODO
    // Исправить ошибку: если в основной задаче нет контрольных точек, то контрольные 
    // точки подзадач не будут посчитаны. Они должны быть посчитаны в любом случае.
    if (!task.checkpoints) {
      return [];
    }

    const taskCheckpoints = task.checkpoints;

    if (task.subtasks) {
      const subtasksCheckpoints = task.subtasks
        .map((subtask) => subtask.checkpoints)
        .flat();

      if (taskCheckpoints) {
        return [...taskCheckpoints, ...subtasksCheckpoints];
      }

      return [...subtasksCheckpoints];
    }

    return taskCheckpoints;
  };

  onMounted(() => {
    hasUnconfirmedCompetence.value = getCheckpoints(props.task).some(
      (checkpoint) => CHECKPOINT_COMPETENCE_SET_STATUS_DEFAULT === checkpoint.competenceStatus
    );
  });

  const onDrawerClose = () => {
    formName.value = null;
  };

  const showForm = (name) => {
    formName.value = name;
  };

  const onUpdated = async () => {
    formName.value = null;
    emit('update');
  };

  const checkIfEditable = computed(() => {
    return (
      !props.disabled &&
      (props.editorUserId ? props.editorUserId === props.task.userId : true)
    );
  });

  const checkIfSubTaskEditable = (subtask) => {
    return (
      !props.disabled &&
      (props.editorUserId
        ? props.editorUserId === props.task.userId ||
          props.editorUserId === subtask.userId
        : true)
    );
  };

  const changeAvailability = async () => {
    const prevIsAvailable = props.task.visibility;
    try {
      await api.projectManagementPage.saveTask({
        id: props.task.id,
        projectId: props.task.projectId,
        userId: props.task.userId,
        description: props.task.name,
        parentId: props.task.parentId,
        isAvailable: !props.task.visibility,
      });

      isVisible.value = !props.task.visibility;
    } catch (error) {
      isVisible.value = prevIsAvailable;
      console.error('error', error);
    }
  };
</script>

<style lang="scss" scoped>
  @use 'task-item';
</style>
